import { Box, Button, Grid, Input, MenuItem, Select, Slider, Typography } from "@mui/material";
import { quat, vec3 } from "gl-matrix";
import { useEffect, useState } from "react";
import Common from "../Refinery/CommonRender";
import { Model, RenderObject } from "../Refinery/Objects/RenderObject";
import { Material } from "../Refinery/Objects/types";
import { ShaderType } from "../Refinery/ShaderManager";
import { useUploadAsset } from "../hooks/useAsset";
import { ASSET_TYPE } from "../types/types";

export type ObjectControlsData = {
  model?: Model;
  material?: Material;
  reset?: boolean;
  parent?: RenderObject;
};


export type ObjectControlsProps = {
  textures: string[];
  possibleParentObjects: RenderObject[];
  onSelectedChange: (value: ObjectControlsData) => void;
  onDelete: () => void;
  onClone: () => void;
  selectedObject?: RenderObject;
};


export function LabeledInput({ label, value, onChange, onBlur, inputProps }: any) {

  return (
    <Box display="flex" alignItems="stretch">
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.primary,
          borderTopLeftRadius: "4px",
          borderBottomLeftRadius: "4px",
          px: 1.5,
          fontWeight: 600,
          fontSize: "0.875rem",
        })}
      >
        {label}
      </Box>

      <Input
        type="number"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        fullWidth
        disableUnderline
        inputProps={inputProps}
        sx={(theme) => ({
          textAlign: "center",
          border: `1px solid ${theme.palette.text.primary}`,
          borderLeft: "none",
          borderTopRightRadius: "4px",
          borderBottomRightRadius: "4px",
          px: 1,
          "& input": {
            padding: "8px 0", // match label height exactly
            textAlign: "center",
          }
        })}
      />
    </Box>
  );
}

export const ObjectControls = (props: ObjectControlsProps) => {
  const { uploadAsset, loading, error } = useUploadAsset();
  const [positionX, setPositionX] = useState<string | undefined>(undefined);
  const [positionY, setPositionY] = useState<string | undefined>(undefined);
  const [positionZ, setPositionZ] = useState<string | undefined>(undefined);

  const [scaleX, setScaleX] = useState<string | undefined>(undefined);
  const [scaleY, setScaleY] = useState<string | undefined>(undefined);
  const [scaleZ, setScaleZ] = useState<string | undefined>(undefined);

  const [rotationX, setRotationX] = useState<string | undefined>(undefined);
  const [rotationY, setRotationY] = useState<string | undefined>(undefined);
  const [rotationZ, setRotationZ] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.selectedObject) {
      setPositionX(props.selectedObject.model.position[0].toFixed(2).toString());
      setPositionY(props.selectedObject.model.position[1].toFixed(2).toString());
      setPositionZ(props.selectedObject.model.position[2].toFixed(2).toString());

      setScaleX(props.selectedObject.model.scale[0].toFixed(2).toString());
      setScaleY(props.selectedObject.model.scale[1].toFixed(2).toString());
      setScaleZ(props.selectedObject.model.scale[2].toFixed(2).toString());

      setRotationX(props.selectedObject.model.rotationInputs.x.toFixed(0).toString());
      setRotationY(props.selectedObject.model.rotationInputs.y.toFixed(0).toString());
      setRotationZ(props.selectedObject.model.rotationInputs.z.toFixed(0).toString());
    }
  }, [props.selectedObject]);


  const handlePositionChange = (value: string, index: number, setter: (v: string) => void) => {
    if (!props.selectedObject) return;

    let newValue = 0;
    setter(value)

    if (value !== '') {
      newValue = parseFloat(value);
    }

    const newPosition = vec3.fromValues(
      props.selectedObject.model.position[0],
      props.selectedObject.model.position[1],
      props.selectedObject.model.position[2]);

    newPosition[index] = newValue;
    const data: ObjectControlsData = {
      model: {
        ...props.selectedObject?.model,
        position: newPosition
      },
      material: props.selectedObject.material,
    };
    props.onSelectedChange(data);
  };

  const handleScaleChange = (value: string, index: number, setter: (v: string) => void) => {
    if (!props.selectedObject) return;

    let newValue = 1;

    if (value !== '') {
      const val = parseFloat(value);
      if (val < 0) {
        setter('1');
        return;
      }
      newValue = val;
    }
    setter(value)

    const newScale = vec3.fromValues(
      props.selectedObject.model.scale[0],
      props.selectedObject.model.scale[1],
      props.selectedObject.model.scale[2]);

    newScale[index] = newValue;
    const data: ObjectControlsData = {
      model: {
        ...props.selectedObject?.model,
        scale: newScale,
      }
    };
    props.onSelectedChange(data);
  };

  const handleRotationChange = (value: string, index: number, setter: (v: string) => void, currentValue?: string) => {
    if (!props.selectedObject) return;

    let newValue = 0;
    setter(value);

    if (value !== '') {
      newValue = parseFloat(value);
    }

    let objectCurrentAxisValues = {
      ...props.selectedObject.model.rotationInputs
    };
    let newRotationQuat = quat.create();
    if (index === 0) {
      objectCurrentAxisValues.x = newValue;
      quat.fromEuler(newRotationQuat, newValue, objectCurrentAxisValues.y, objectCurrentAxisValues.z);
    } else if (index === 1) {
      objectCurrentAxisValues.y = newValue;
      quat.fromEuler(newRotationQuat, objectCurrentAxisValues.x, newValue, objectCurrentAxisValues.z);
    } else if (index === 2) {
      objectCurrentAxisValues.z = newValue;
      quat.fromEuler(newRotationQuat, objectCurrentAxisValues.x, objectCurrentAxisValues.z, newValue);
    }

    const data: ObjectControlsData = {
      model: {
        ...props.selectedObject.model,
        rotation: newRotationQuat,
        rotationInputs: objectCurrentAxisValues,
      }
    };
    props.onSelectedChange(data);
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: ASSET_TYPE) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const metadata = { name: file.name, type };
    const result = await uploadAsset(file, metadata);

    if (result.success) {
      console.log("Uploaded successfully:", result.data);
    } else {
      console.error("Upload error:", result.message);
    }
  };

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Grid>
          <Typography color="textSecondary">Parent</Typography>
        </Grid>
        <Grid>
          <Select
            fullWidth
            value={props.selectedObject?.parent?.name ?? ''}
            onChange={(e) => {
              const parentName = e.target.value as string;
              // look through parentable objects, call on change with the parent
              const parentObject = props.possibleParentObjects.find(v => v.name === parentName);
              props.onSelectedChange({ parent: parentObject });
            }}
          >
            {props.possibleParentObjects.map(o => {
              return <MenuItem value={o.name}>{o.name}</MenuItem>
            })}
          </Select>
        </Grid>
      </Grid>
      <Grid size={12} spacing={2}>
        {/* left box */}
        <Grid container spacing={2}>
          <Grid size={12}>
            <Grid container spacing={1}>
              <Grid size={12}>
                <Typography color="textSecondary">Position</Typography>
              </Grid>
              <Grid size={4}>
                <LabeledInput
                  label="X"
                  value={positionX}
                  onChange={(e: any) => handlePositionChange(e.target.value, 0, setPositionX)}
                  onBlur={() => positionX === "" && setPositionX("0")}
                />
              </Grid>
              <Grid size={4}>
                <LabeledInput
                  label="Y"
                  value={positionY}
                  onChange={(e: any) => handlePositionChange(e.target.value, 1, setPositionY)}
                  onBlur={() => positionY === "" && setPositionY("0")}
                />
              </Grid>
              <Grid size={4}>
                <LabeledInput
                  label="Z"
                  value={positionZ}
                  onChange={(e: any) => handlePositionChange(e.target.value, 2, setPositionZ)}
                  onBlur={() => positionZ === "" && setPositionZ("0")}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container direction='row' spacing={1}>
              <Grid size={12}>
                <Typography color="textSecondary">Rotation (in degrees)</Typography>
              </Grid>
              <Grid size={4}>
                <LabeledInput
                  label="X"
                  value={rotationX}
                  onChange={(e: any) => handleRotationChange(e.target.value, 0, setRotationX, rotationX)}
                  onBlur={() => rotationX === "" && setRotationX("0")}
                />
              </Grid>
              <Grid size={4}>
                <LabeledInput
                  label="Y"
                  value={rotationY}
                  onChange={(e: any) => handleRotationChange(e.target.value, 1, setRotationY, rotationY)}
                  onBlur={() => rotationY === "" && setRotationY("0")}
                />
              </Grid>
              <Grid size={4}>
                <LabeledInput
                  label="Z"
                  value={rotationZ}
                  onChange={(e: any) => handleRotationChange(e.target.value, 2, setRotationZ, rotationZ)}
                  onBlur={() => rotationZ === "" && setRotationZ("0")}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container direction='row' spacing={1}>
              <Grid size={12}>
                <Typography color="textSecondary">Scale</Typography>
              </Grid>
              <Grid size={4}>
                <LabeledInput
                  label="X"
                  value={scaleX}
                  onBlur={() => {
                    if (scaleX === "") {
                      setScaleX("1");
                    }
                  }}
                  inputProps={{
                    min: 1,
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleScaleChange(event.target.value, 0, setScaleX);
                  }}
                />
              </Grid>

              <Grid size={4}>
                <LabeledInput
                  label="Y"
                  value={scaleY}
                  onBlur={() => {
                    if (scaleY === "") {
                      setScaleY("1");
                    }
                  }}
                  inputProps={{
                    min: 1,
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleScaleChange(event.target.value, 1, setScaleY);
                  }}
                />
              </Grid>

              <Grid size={4}>
                <LabeledInput
                  label="Z"
                  value={scaleZ}
                  onBlur={() => {
                    if (scaleZ === "") {
                      setScaleZ("1");
                    }
                  }}
                  inputProps={{
                    min: 1,
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleScaleChange(event.target.value, 2, setScaleZ);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid size={12}>
        {/* Colour */}
        {/* Alpha slider */}
        {/* Shader selector */}
        <Grid container direction='column' spacing={2}>
          <Grid size={12}>
            <Typography color="textSecondary">Colour</Typography>
          </Grid>
          <Grid size={12}>
            <Input
              type='color'
              sx={{ width: '40px' }}
              value={Common.rgbToHex(props.selectedObject?.material?.diffuse ?? vec3.create())}
              onChange={(e) => {
                if (!props.selectedObject || !props.selectedObject.material) {
                  return;
                }
                const hexVal = e.target.value;
                props.onSelectedChange({
                  material: {
                    ...props.selectedObject?.material,
                    diffuse: Common.hexToRGB(hexVal),
                  }
                })
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography color="textSecondary">Alpha</Typography>
          </Grid>
          <Grid size={12}>
            <Slider
              value={props.selectedObject?.material?.alpha !== undefined ? props.selectedObject?.material?.alpha * 100 : 0}
              onChange={(_, value) => {
                if (!props.selectedObject || !props.selectedObject.material) {
                  return;
                }
                props.onSelectedChange({
                  material: {
                    ...props.selectedObject?.material,
                    alpha: value / 100,
                  },
                  reset: true,
                });
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography color="textSecondary">Shader Type</Typography>
          </Grid>
          <Grid size={12}>
            <Select
              autoWidth
              value={props.selectedObject?.material?.shaderType as ShaderType}
              onChange={(e) => {
                if (!props.selectedObject || !props.selectedObject.material) {
                  return;
                }
                props.onSelectedChange({
                  ...props.selectedObject,
                  material: {
                    ...props.selectedObject?.material,
                    shaderType: e.target.value as ShaderType,
                  },
                  reset: true,
                });
              }}
            >
              {Object.entries(ShaderType).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {key.replaceAll("_", " ")}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </Grid>
      {/* Textures */}
      <Grid size={12} container spacing={2}>
        <Grid size={12}>
          <Typography color="textSecondary">Diffuse Texture</Typography>
        </Grid>
        <Grid size={12}>
          <Select
            fullWidth
            value={props.selectedObject?.material?.diffuseTexture?.name ?? ''}
            onChange={(e) => {
              if (!props.selectedObject || !props.selectedObject.material) {
                return;
              }
              props.onSelectedChange({
                ...props.selectedObject,
                material: {
                  ...props.selectedObject?.material,
                  diffuseTexture: {
                    name: e.target.value as string,
                  }
                },
                reset: true,
              })
            }}
          >
            {props.textures.map(t => {
              return <MenuItem value={t}>{t}</MenuItem>
            })}
          </Select>
        </Grid>
        <Grid size={12}>
          <Typography color="textSecondary">Normal Texture</Typography>
        </Grid>
        <Grid size={12}>
          <Select
            fullWidth
            value={props.selectedObject?.material?.normalTexture?.name ?? ''}
            onChange={(e) => {
              if (!props.selectedObject || !props.selectedObject.material) {
                return;
              }
              props.onSelectedChange({
                ...props.selectedObject,
                material: {
                  ...props.selectedObject?.material,
                  normalTexture: {
                    name: e.target.value as string,
                  }
                },
                reset: true,
              })
            }}
          >
            {props.textures.map(t => {
              return <MenuItem value={t}>{t}</MenuItem>
            })}
          </Select>
        </Grid>
      </Grid>
      {/* Options */}
      <Grid size={12} container spacing={2}>
        <Grid size={12}>
          <Typography color="textSecondary">Options</Typography>
        </Grid>
        <Grid container spacing={2} size={12}>
          <Button
            onClick={async () => {
              props.onClone();
            }}
            variant='outlined'
          >Clone</Button>
          <Button
            color="error"
            onClick={async () => {
              props.onDelete();
            }}
            variant='outlined'
          >Delete</Button>
        </Grid>
      </Grid>
    </Grid>
  )
};
